import { apiFetchJson } from "./apiFetchRoute";
import type { RemoteConnectionPage } from "../generated/types";
import type {
  EnumRemoteConnectionKind,
  EnumRemoteConnectionStatus,
} from "../generated/enums";

/** Taille de page par défaut. Le serveur plafonne à 200, quoi qu'on demande. */
export const REMOTE_CONNECTIONS_PAGE_SIZE = 50;

/** Critères de l'écran « Historique ». Un champ vide = filtre inactif. */
export type RemoteConnectionFilters = {
  /**
   * Recherche GLOBALE, faite en SQL côté serveur sur toutes les colonnes textuelles de
   * la ligne (un bloc par mot, tous les mots requis). Pas en mémoire : l'historique est
   * paginé côté serveur, une recherche locale ne verrait que la page affichée.
   */
  q?: string;
  uuid?: string;
  idAccount?: number;
  /**
   * Entreprise figée dans la ligne. On filtre par ID et non par nom : `company_name`
   * vient de l'API USERS (autre base) et n'existe pas en SQL côté BACK. La ligne porte
   * déjà l'id, le clic l'envoie donc directement.
   */
  idCompany?: number;
  status?: EnumRemoteConnectionStatus | "";
  kind?: EnumRemoteConnectionKind | "";
  /** Nom du produit : recherche partielle (jointure `device`, seul libellé en base). */
  deviceName?: string;
  dateFrom?: string; // AAAA-MM-JJ
  dateTo?: string; // AAAA-MM-JJ
};

/**
 * Récupère une page de l'historique des connexions à distance (admin/PSC).
 *
 * Pagination SERVEUR : contrairement aux autres tableaux du projet, cet écran ne peut pas
 * tout charger — la table atteint ~90 000 lignes à 6 mois de rétention. `total` est le
 * nombre de lignes correspondant aux filtres sans la pagination, il sert à afficher
 * « 1–50 sur 1234 » et à savoir s'il reste une page.
 *
 * `?? { total: 0, rows: [] }` : la route répond **204 No Content** quand il n'y a aucune
 * ligne, et `apiFetchJson` renvoie alors `undefined`. Sans ce repli, lire `.rows` lèverait
 * un TypeError — bug déjà rencontré sur `fetchDevices`.
 */
export const fetchRemoteConnections = async (
  filters: RemoteConnectionFilters = {},
  page = 0,
  pageSize = REMOTE_CONNECTIONS_PAGE_SIZE
): Promise<{ page: RemoteConnectionPage; error: string | null }> => {
  try {
    const result = (await apiFetchJson("getAdminRemoteconnections", undefined, {
      query: {
        q: filters.q?.trim() || undefined,
        uuid: filters.uuid?.trim() || undefined,
        id_account: filters.idAccount || undefined,
        id_company: filters.idCompany || undefined,
        status: filters.status || undefined,
        kind: filters.kind || undefined,
        device_name: filters.deviceName?.trim() || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        limit: pageSize,
        offset: page * pageSize,
      },
    })) as RemoteConnectionPage | undefined;

    return {
      page: result ?? { total: 0, rows: [] },
      error: null,
    };
  } catch (e) {
    return {
      page: { total: 0, rows: [] },
      error: "remote_connections_load_error",
    };
  }
};
